import React, { useState } from 'react';
import {
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  CardActionArea,
  Box,
  Button,
  Fade,
  Container,
  Divider,
  Grid,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Form } from 'react-final-form';
import RestaurantIcon from '@mui/icons-material/Restaurant';
import DiningIcon from '@mui/icons-material/Dining';
import { ColorlibConnector, ColorlibStepIcon } from '../../components/reusable/StyledStepper';
import FormControlledTextField from '../../components/reusable/FormControlledTextField';
import * as api from '../../constants/api';
import { CateringFormValues } from '../../constants/interfaces';
import { cateringValidation } from '../../constants/validationSchemas';
import { clampEventDate, formatPhoneNumber, getMinEventDate, normalizePhoneNumber, shouldPreventPhonePrefixDeletion } from '../../constants/utils';
import { useSnackbar } from '../../contexts/SnackbarContext';

export default function Catering() {
  const [activeStep, setActiveStep] = useState(0);
  const [selectedOption, setSelectedOption] = useState<'small' | 'large' | null>(null);
  const [hasSubmittedSuccessfully, setHasSubmittedSuccessfully] = useState(false);
  const { snackbarDispatch } = useSnackbar();
  const minEventDate = getMinEventDate();

  const getInitialValues = (): CateringFormValues => ({
    groupSize: selectedOption ?? undefined,
    name: '',
    email: '',
    phone: '+1-',
    numberOfPeople: '',
    date: '',
    address: '',
    allergies: '',
  });

  const handleSelectOption = (option: 'small' | 'large') => {
    setHasSubmittedSuccessfully(false);
    setSelectedOption(option);
    setActiveStep(1);
  };

  const handleSubmit = async (values: CateringFormValues) => {
    const { groupSize, ...payload } = values;

    try {
      await api.sendCateringRequest(payload);
      snackbarDispatch({ type: 'setSnackbar', payload: { message: 'Catering request submitted!', error: false } });
      setHasSubmittedSuccessfully(true);
      return true;
    } catch {
      snackbarDispatch({ type: 'setSnackbar', payload: { message: 'Error submitting catering request', error: true } });
      setHasSubmittedSuccessfully(false);
      return false;
    }
  };

  return (
    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 5, minHeight: '80vh' }}>
      <Container maxWidth="md">
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.5, md: 4 },
            borderRadius: 3,
            border: '1px solid rgba(25, 118, 210, 0.18)',
            boxShadow: '0 18px 42px rgba(15, 23, 42, 0.09)',
          }}
        >
          <Stepper alternativeLabel activeStep={activeStep} connector={<ColorlibConnector />}>
            <Step>
              <StepLabel slots={{ stepIcon: ColorlibStepIcon }}>
                <Typography variant="h3">GROUP SIZE</Typography>
              </StepLabel>
            </Step>
            <Step>
              <StepLabel slots={{ stepIcon: ColorlibStepIcon }}>
                <Typography variant="h3">EVENT DETAILS</Typography>
              </StepLabel>
            </Step>
          </Stepper>

      <Fade in={activeStep === 0} timeout={500}>
        <Box sx={{ mt: 4, display: activeStep === 0 ? 'block' : 'none' }}>
          <Typography variant="h5" sx={{ mb: 3, textAlign: 'center' }}>
            How many people will be attending?
          </Typography>
          <Box sx={{ display: 'flex', gap: 3, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Card
              sx={{
                width: 250,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                transform: selectedOption === 'small' ? 'scale(1.05) translateX(-100px)' : 'scale(1)',
                opacity: selectedOption === 'small' ? 1 : selectedOption ? 0.5 : 1,
              }}
            >
              <CardActionArea onClick={() => handleSelectOption('small')} sx={{ p: 2 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <RestaurantIcon sx={{ fontSize: 80, mb: 2, color: 'primary.main' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Less than 25 People
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>

            <Card
              sx={{
                width: 250,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                transform: selectedOption === 'large' ? 'scale(1.05) translateX(100px)' : 'scale(1)',
                opacity: selectedOption === 'large' ? 1 : selectedOption ? 0.5 : 1,
              }}
            >
              <CardActionArea onClick={() => handleSelectOption('large')} sx={{ p: 2 }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <DiningIcon sx={{ fontSize: 80, mb: 2, color: 'primary.main' }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    More than 25 People
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          </Box>
        </Box>
      </Fade>

          <Fade in={activeStep === 1} timeout={500}>
            <Box sx={{ mt: 4, display: activeStep === 1 ? 'block' : 'none' }}>
              <Typography variant="body2" sx={{ mb: 3, textAlign: 'center' }}>
                Tell us about your event
              </Typography>

          {/*
            Legacy form commented out from the UI while the replacement ships.
            <Form onSubmit={handleSubmit}>
              name, email, phone, numberOfPeople, date, address, allergies
            </Form>
          */}

              <Form
                key={selectedOption ?? 'none'}
                onSubmit={async (values, form) => {
                  const wasSuccessful = await handleSubmit(values as CateringFormValues);
                  if (wasSuccessful) {
                    form.restart(getInitialValues());
                  }
                }}
                validate={cateringValidation}
                initialValues={getInitialValues()}
                render={({ handleSubmit: submitForm, submitting, form }) => (
                  <form onSubmit={submitForm} noValidate>
                    <Stack spacing={2.25}>
                      <Typography variant="h6" color="text.secondary" sx={{ textAlign: 'center' }}>
                        Group size: {selectedOption === 'large' ? 'More than 25 people' : 'Less than 25 people'}
                      </Typography>

                      <Divider />

                      <Grid container columnSpacing={2} rowSpacing={3}>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <FormControlledTextField variant="outlined" name="name" label="Name" fullWidth required />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <FormControlledTextField variant="outlined" name="email" label="Email" type="email" fullWidth required />
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <FormControlledTextField
                          variant="outlined"
                          name="phone"
                          label="Phone"
                          placeholder="+1-XXX-XXX-XXXX"
                          format={(value?: string) => formatPhoneNumber(value as string | undefined)}
                          parse={(value?: string) => normalizePhoneNumber(value)}
                          onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                            form.change('phone', normalizePhoneNumber(event.target.value));
                          }}
                          onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
                            if (shouldPreventPhonePrefixDeletion(event)) {
                              event.preventDefault();
                            }
                          }}
                          fullWidth
                          required
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <FormControlledTextField
                          variant="outlined"
                          name="numberOfPeople"
                          label="Number of People"
                          type="number"
                          fullWidth
                          required
                        />
                      </Grid>

                      <Grid size={{ xs: 12, md: 6 }}>
                        <FormControlledTextField
                          variant="outlined"
                          name="date"
                          label="Date"
                          type="date"
                          onChange={(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
                            form.change('date', clampEventDate(event.target.value, minEventDate));
                          }}
                          slotProps={{
                            inputLabel: { shrink: true },
                            htmlInput: { min: minEventDate },
                          }}
                          fullWidth
                          required
                        />
                      </Grid>
                      <Grid size={{ xs: 12, md: 6 }}>
                        <FormControlledTextField variant="outlined" name="address" label="Address" fullWidth required />
                      </Grid>

                      <Grid size={{ xs: 12 }}>
                        <FormControlledTextField
                          variant="outlined"
                          name="allergies"
                          label="Allergies / Dietary Restrictions"
                          multiline
                          rows={4}
                          fullWidth
                        />
                      </Grid>
                      </Grid>

                      <Box sx={{ display: 'flex', gap: 1.5, justifyContent: { xs: 'center', md: 'flex-end' } }}>
                        <Button
                          variant="outlined"
                          color="error"
                          onClick={() => {
                            setHasSubmittedSuccessfully(false);
                            setActiveStep(0);
                            setSelectedOption(null);
                          }}
                        >
                          Back
                        </Button>
                        <Button type="submit" variant="contained" color="primary" disabled={submitting}>
                          Submit Request
                        </Button>
                      </Box>
                    </Stack>
                  </form>
                )}
              />

              {hasSubmittedSuccessfully && (
                <Box
                  sx={{
                    maxWidth: 560,
                    mx: 'auto',
                    mt: 2,
                    px: 2,
                    py: 1.5,
                    borderRadius: 1.5,
                    backgroundColor: 'rgba(96, 165, 250, 0.2)',
                    border: '1px solid rgba(96, 165, 250, 0.5)',
                    color: 'primary.dark',
                    fontSize: '0.9rem',
                    lineHeight: 1.4,
                    textAlign: 'center',
                  }}
                >
                  catering request submitted!
                </Box>
              )}
            </Box>
          </Fade>
        </Paper>
      </Container>
    </Box>
  );
}
